// @flow

import { action, observable } from "mobx";

import Store from "app/stores/Store";
import AppearanceStore from "app/stores/AppearanceStore";
import FolderService from "app/services/FolderService";
import Folder from "app/models/Folder";
import VideoStore from "app/stores/VideoStore";
import Video from "app/models/Video";

class FolderStore extends Store {
    @observable selectedId: ?Number;
    @observable selected: ?Folder;
    @observable folders: ?Folder[];
    @observable isAdding: Boolean = false;
    @observable isEditing: Boolean = false;
    @observable isDeleting: Boolean = false;
    @observable isSaving: Boolean = false;
    @observable name: String = "";
    @observable changingId: Number;

    @action
    select(folder: ?Folder) {
        this.selectedId = folder ? folder.id : null;
        this.selected = folder;
    }

    @action.bound
    setName (name: string) {
        this.name = name;
    }

    @action.bound
    setFolders (folders: ?Folder[]) {
        this.folders = folders;
    }

    @action.bound
    startAdding() {
        this.cancelSaving();
        this.cancelEditing()
        this.cancelDeleting();
        this.isAdding = true;
    }

    @action.bound
    startEditing(folder: Folder) {
        this.cancelSaving();
        this.cancelAdding();
        this.cancelDeleting();
        this.name = folder.name;
        this.changingId  = folder.id;
        this.isEditing = true;
    }

    @action.bound
    startDeleting(folder: Folder) {
        this.cancelSaving();
        this.cancelAdding();
        this.cancelEditing();
        this.name = folder.name;
        this.changingId = folder.id;
        this.isDeleting = true;
    }

    @action.bound
    cancelAdding() {
        this.cancelSaving();
        this.name = "";
        this.isAdding = false;
    }

    @action.bound
    cancelEditing() {
        this.cancelSaving();
        this.changingId  = null;
        this.name = "";
        this.isEditing = false;
    }

    @action.bound
    cancelDeleting() {
        this.cancelSaving();
        this.changingId = false;
        this.isDeleting = false;
    }

    @action.bound
    cancelSaving() {
        this.isSaving = false;
    }

    @action.bound
    save () {
        this.isSaving = true;
        if (this.name.length === 0) {
            AppearanceStore.notifyError('Folder name is empty.');
            this.isSaving = false;
            return null;
        }

        if (this.isEditing) {
            this.updateName(this.changingId , this.name);
        } else if (this.isAdding) {
            this.create(this.name);
        }
    }

    @action.bound
    create(name: string): Promise<*> {
        return FolderService.create(name).then(
            ({ data }) => {
                this.cancelAdding();
                this.cancelSaving();
                this.setFolders(Folder.makeArray(data.folders));
                AppearanceStore.notifySuccess(data.message);
            },
            ({ response }: any) => {
                this.cancelSaving();
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    @action.bound
    updateName(id: number, name: string): Promise<*> {
        FolderService.updateName(id, name).then(
            ({ data }) => {
                this.cancelEditing();
                this.cancelSaving();
                this.setFolders(Folder.makeArray(data.folders));
                AppearanceStore.notifySuccess(data.message);
            },
            ({ response }: any) => {
                this.cancelSaving();
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    @action.bound
    delete () {
        this.isSaving = true;
        return FolderService.delete(this.changingId).then(
            ({ data }) => {
                this.cancelDeleting();
                this.cancelSaving();
                this.setFolders(Folder.makeArray(data.folders));
                VideoStore.setVideos(Video.makeArray(data.videos));
                AppearanceStore.notifySuccess(data.message);
            },
            ({ response }: any) => {
                this.cancelDeleting();
                this.cancelSaving();
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }
}

export default new FolderStore();



// WEBPACK FOOTER //
// ./src/app/stores/FolderStore.js